import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Layout } from 'ui/layout/Layout'
import { AppBarAdmin } from 'ui/partials/AppBar'
import AdminContentWrapper from 'ui/layout/AdminContentWrapper'
import AdminSidebar, {
  AdminSidebarSelectRouteProps,
} from 'ui/partials/AdminSidebar'
import type {
  InformationToMed,
  MedicalInstitutionProps,
  PreReservation,
  PreReservationForm,
} from 'types/med'

import type { DepartmentListItemProps } from 'types/common'

import Modal from 'ui/layout/Modal'
import { ConfirmContent } from 'ui/partials/ConfirmContent'

import PreReservationList, {
  PreReservationListProps,
} from './PreReservationList'
import PreReservationRead from './PreReservationRead'
import PreReservationCreate from './PreReservationCreate'
import { Button } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import type { Theme } from '@mui/material/styles'
import { sideBarWidth } from 'ui/theme'
import PreReservationControlBar from './PreReservationControlBar'
import Snackbar from 'ui/utils/Snackbar'
import { ApiErrorWithMessage } from 'app/client'

const useStyles = makeStyles()((theme: Theme) => ({
  leftBottomFloating: {
    position: 'absolute',
    left: 0,
    bottom: 68,
    width: sideBarWidth,
    [theme.breakpoints.down('md')]: {
      width: 240,
    },
  },
  addButtonWrapper: {
    padding: theme.spacing(2),
  },
  addButton: {
    padding: theme.spacing(1.5),
    borderRadius: 24,
  },
}))

export type PreReservationsProps = {
  /** 医療機関設定 */
  med: MedicalInstitutionProps
  /** 診療科目概要リスト */
  departments: DepartmentListItemProps[]
  /** 閲覧中の仮予約 */
  currentPreReservation?: PreReservation
  /** 新規追加の時 */
  creating: boolean
  /** 新規仮予約送信,成功時コールバック実行 */
  onSendSms: (data: PreReservationForm | any) => Promise<void>
  /** SMS再送信クリック */
  onResendSms: () => Promise<void>
  /** 仮予約キャンセル */
  onCancel: () => void
  /** 仮予約新規作成キャンセル */
  onDiscardCreate: () => void
  /** 仮予約新規作成ボタンクリック */
  onClickCreate: () => void
  /** サイドバーでのページ切り替え */
  selectRoute: AdminSidebarSelectRouteProps
  /** サイドバーprops */
  preReservationSubmenu: PreReservationListProps

  update?: InformationToMed
}

const PreReservations: React.FC<PreReservationsProps> = ({
  med,
  currentPreReservation,
  creating,
  departments,
  onSendSms,
  onResendSms,
  onCancel,
  onClickCreate,
  selectRoute,
  preReservationSubmenu,
  onDiscardCreate,
  update,
}) => {
  const { t } = useTranslation()
  const { classes } = useStyles()

  const [smsSending, setSmsSending] = useState<boolean>(false)
  const [openCancelModal, setOpenCancelModal] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState<
    React.ReactNode | undefined
  >()

  const [snackBar, setSnackBar] = useState<{
    open: boolean
    message: string
    error: boolean
  }>({ open: false, message: '', error: false })

  const onSuccess = () =>
    setSnackBar({
      open: true,
      message: t('送信が完了しました'),
      error: false,
    })

  const sidebarContent = (
    <AdminSidebar med={med} selectRoute={selectRoute}>
      <PreReservationList {...preReservationSubmenu} />
    </AdminSidebar>
  )

  const errorModal = (
    <Modal open={!!errorMessage} handleClose={() => setErrorMessage(undefined)}>
      <ConfirmContent
        onSubmit={() => {
          setErrorMessage(undefined)
        }}
        title={errorMessage}
        submitLabel={t('閉じる')}
      />
    </Modal>
  )

  const cancelModal = (
    <Modal open={openCancelModal} handleClose={() => setOpenCancelModal(false)}>
      <ConfirmContent
        onCancel={() => setOpenCancelModal(false)}
        onSubmit={() => {
          setOpenCancelModal(false)
          onCancel()
        }}
        title={t('仮予約を取り消しますか？')}
        submitLabel={t('取り消す')}
      />
    </Modal>
  )

  return (
    <Layout header={<AppBarAdmin update={update} />}>
      <AdminContentWrapper
        sidebar={sidebarContent}
        controlBar={
          currentPreReservation && (
            <PreReservationControlBar
              onClickCancel={() => setOpenCancelModal(true)}
              onClickReSendSms={() => {
                setSmsSending(true)
                const result = onResendSms()
                result
                  .then(() => {
                    onSuccess()
                  })
                  .catch((e) => {
                    if (
                      e instanceof ApiErrorWithMessage &&
                      e.has({
                        type: 'validation_error',
                        key: 'provisional_reservation_datetime_end',
                      })
                    ) {
                      setErrorMessage(
                        t('errors.再送信が可能な時刻を過ぎています。')
                      )
                    } else {
                      setSnackBar({
                        open: true,
                        message: t('errors.送信に失敗しました。'),
                        error: true,
                      })
                    }
                  })
                  .finally(() => setSmsSending(false))
              }}
              preReservation={currentPreReservation}
              sending={smsSending}
            />
          )
        }
      >
        {currentPreReservation && (
          <PreReservationRead
            preReservation={currentPreReservation}
            departments={departments}
          />
        )}
        {creating ? (
          <PreReservationCreate
            departments={departments}
            onSubmit={(data) => {
              return new Promise((resolve, reject) => {
                const result = onSendSms(data)
                result
                  .then(() => {
                    onSuccess()
                    resolve()
                  })
                  .catch((e) => {
                    if (
                      e instanceof ApiErrorWithMessage &&
                      e.has({
                        type: 'validation_error',
                        key: 'provisional_reservation_datetime_end',
                      })
                    ) {
                      setErrorMessage(
                        t(
                          'errors.仮予約が可能な時刻を過ぎています。日時をもう一度選択してください。'
                        )
                      )
                    } else {
                      setSnackBar({
                        open: true,
                        message: t('errors.送信に失敗しました。'),
                        error: true,
                      })
                    }
                    reject()
                  })
                  .finally(() => setSmsSending(false))
              })
            }}
            onDiscard={() => {
              setTimeout(() => onDiscardCreate(), 0)
            }}
          />
        ) : (
          <div className={classes.leftBottomFloating}>
            <div className={classes.addButtonWrapper}>
              <Button
                className={classes.addButton}
                onClick={onClickCreate}
                color={'primary'}
                variant={'contained'}
                fullWidth
              >
                {t('仮予約作成')}
              </Button>
            </div>
          </div>
        )}
      </AdminContentWrapper>
      <Snackbar
        open={snackBar.open}
        handleClose={() =>
          setSnackBar((prev) => {
            return { ...prev, open: false }
          })
        }
        error={snackBar.error}
        message={snackBar.message}
      />
      {errorModal}
      {cancelModal}
    </Layout>
  )
}
export default PreReservations
